

'use client'

import React, { useState } from 'react';
import { Button, Spinner } from 'flowbite-react';
// import Image from 'next/image';
import { AiOutlineEdit, AiFillDelete, AiOutlineCheckCircle } from 'react-icons/ai';
import { FaRegShareFromSquare } from 'react-icons/fa6';

import { Share } from '../share';
import type { EventPromoter } from '../../models';

// --- Type Definitions for Props ---
interface Props {
    promoters: EventPromoter[];
    // A flag to determine if management controls should be shown.
    canManage?: boolean;

    // Callbacks for all possible user actions.
    onAddPromoter?: () => void | undefined;
    onEditPromoter?: (promoter: EventPromoter) => void| undefined;
    onBlockPromoter?: (promoterId: string) => Promise<void>| undefined;
    onUnblockPromoter?: (promoter: EventPromoter) => Promise<void>| undefined;
}

const EventPromoters: React.FC<Props> = ({
    promoters = [],
    canManage = false,
    onAddPromoter,
    onEditPromoter,
    onBlockPromoter,
    onUnblockPromoter,
}) => {
    // State to track the ID of the promoter being updated (blocked/unblocked).
    const [loadingPromoterId, setLoadingPromoterId] = useState<string | null>(null);

    // Generic handler to wrap async actions and manage loading state.
    const handleAction = async (promoter: EventPromoter, action: 'block' | 'unblock') => {
        setLoadingPromoterId(promoter.id);
        try {
            if (action === 'block' && onBlockPromoter) {
                await onBlockPromoter(promoter.id);
            } else if (action === 'unblock' && onUnblockPromoter) {
                await onUnblockPromoter(promoter);
            }
        } catch (error) {
            console.error(`Parent failed to ${action} promoter:`, error);
        } finally {
            setLoadingPromoterId(null);
        }
    };

    return (
        <div className="bg-white py-16 sm:py-24">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-4xl text-center">
                    <h2 className="text-base font-semibold leading-7 text-indigo-600">Event Promoters</h2>
                    <p className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                        Manage Your Promoters
                    </p>
                </div>
                
                {canManage && onAddPromoter && (
                    <div className="mt-10 flex items-center justify-center">
                        <Button onClick={onAddPromoter}>Add Promoter</Button>
                    </div>
                )}

                {promoters.length > 0 ? (
                    <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                        {promoters.map((promoter) => (
                            <div key={promoter.id} className="flex flex-col rounded-3xl bg-white p-8 ring-1 ring-gray-200">
                                <div className="flex-grow">
                                    <h3 className="text-lg font-semibold leading-8 text-gray-900">{promoter.name}</h3>
                                    <p className="mt-1 text-sm text-gray-600">@{promoter.pseudo}</p>
                                    <div className="mt-6 flex justify-center">
                                        <img src={promoter.qrCode as string} alt={`QR Code for ${promoter.name}`} width={192} height={192} />
                                    </div>
                                    <div className="mt-6 flex justify-around text-center">
                                        <div>
                                            <p className="text-2xl font-bold text-gray-900">{promoter.tickets}</p>
                                            <p className="text-xs text-gray-600">Tickets Sold</p>
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold text-gray-900">${promoter.cash_balance}</p>
                                            <p className="text-xs text-gray-600">Revenue</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 text-center flex justify-center">
                                        <Share shareData={{ title: "Share Promoter Link", url: promoter.link }}>
                                            <FaRegShareFromSquare /> Share Link
                                        </Share>
                                    </div>
                                </div>
                                
                                {canManage && promoter.pseudo !== 'default' && (
                                    <div className="mt-6 border-t pt-4">
                                        <div className="flex divide-x divide-gray-200">
                                            {onEditPromoter && <div className="flex w-0 flex-1"><button onClick={() => onEditPromoter(promoter)} className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-50"><AiOutlineEdit className="h-5 w-5 text-gray-400" />Edit</button></div>}
                                            
                                            {promoter.status === 'active' ? (
                                                onBlockPromoter && <div className="-ml-px flex w-0 flex-1"><button onClick={() => handleAction(promoter, 'block')} disabled={loadingPromoterId === promoter.id} className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-50">{loadingPromoterId === promoter.id ? <Spinner size="sm"/> : <><AiFillDelete className="h-5 w-5 text-red-400" />Block</>}</button></div>
                                            ) : (
                                                onUnblockPromoter && <div className="-ml-px flex w-0 flex-1"><button onClick={() => handleAction(promoter, 'unblock')} disabled={loadingPromoterId === promoter.id} className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900 hover:bg-gray-50">{loadingPromoterId === promoter.id ? <Spinner size="sm"/> : <><AiOutlineCheckCircle className="h-5 w-5 text-green-400" />Unblock</>}</button></div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="mt-10 text-center text-gray-500">You have not added any promoters for this event yet.</p>
                )}
            </div>
        </div>
    );
};

export default EventPromoters;